import React from "react";
import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { Computer, Refresh } from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";

interface User {
  id: number;
  email?: string;
  username?: string;
  firstName?: string;
  lastName?: string;
}

interface Team {
  id: number;
  name?: string;
  slug?: string;
}

interface TeamNavProps {
  user: User | null;
  team: Team | null;
  isTeamAdmin?: boolean;
  activeTab?: string;
  onNavigate?: () => void; // For closing mobile drawers
}

export default function TeamNav({
  user,
  team,
  isTeamAdmin = false,
  activeTab,
  onNavigate,
}: TeamNavProps) {
  const navigate = useNavigate();
  const location = useLocation();

  const handleNavigate = (path: string) => {
    navigate(path);
    onNavigate?.(); // Close mobile drawer if provided
  };

  // Enhanced active path detection
  const isActive = (path: string) => {
    const currentPath = location.pathname;

    // Use activeTab prop if provided (for server-side rendered pages)
    if (activeTab) {
      const tabPathMap: Record<string, string[]> = {
        "example-app": ["/example"],
        "subscription-demo": ["/subscription/demo"],
        subscription: ["/subscription"],
      };

      const paths = tabPathMap[activeTab] || [];
      return paths.some((tabPath) => currentPath.includes(tabPath));
    }

    // Client-side path matching
    return currentPath.includes(path);
  };

  const NavItem = ({
    icon: Icon,
    text,
    path,
    adminOnly = false,
  }: {
    icon: React.ElementType;
    text: string;
    path: string;
    adminOnly?: boolean;
  }) => {
    // Hide admin-only items if user is not a team admin
    if (adminOnly && !isTeamAdmin) {
      return null;
    }

    const active = isActive(path);

    return (
      <ListItem disablePadding>
        <ListItemButton
          onClick={() => handleNavigate(path)}
          selected={active}
          sx={{
            pl: 3,
            "&.Mui-selected": {
              backgroundColor: "rgba(25, 118, 210, 0.12)",
              "&:hover": {
                backgroundColor: "rgba(25, 118, 210, 0.16)",
              },
            },
            ...(active && {
              borderLeft: 3,
              borderLeftColor: "readable.link",
              backgroundColor: "rgba(25, 118, 210, 0.12)",
            }),
          }}
        >
          <ListItemIcon sx={{ minWidth: 40 }}>
            <Icon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={text} />
        </ListItemButton>
      </ListItem>
    );
  };

  // Don't render anything if no team context
  if (!team || !user) {
    return null;
  }

  return (
    <List sx={{ py: 0 }}>
      {/* Subscription Demo - admin only */}
      <NavItem
        icon={Computer}
        text="Subscription Demo"
        path={`/a/${team.slug}/subscription/demo`}
        adminOnly={true}
      />

      {/* Subscription - admin only */}
      <NavItem
        icon={Refresh}
        text="Subscription"
        path={`/a/${team.slug}/subscription`}
        adminOnly={true}
      />
    </List>
  );
}
